import { useCallback, useEffect } from "react";
import { UploadFile } from "antd";
import { useApiUrl } from "@refinedev/core";
import { useUploadFiles } from "#src/hooks";
import { DocumentInput } from "types/analytics";
import { uploadFileKey } from "pages/create-experience/helpers/fileList";
import {
  MediaProject,
  ProjectTag,
  ProjectInstanceSettingsRequest,
} from "pages/media/types";
import { ProjectType } from "../types";
import { useProjectCreation } from "./useProjectCreation";

interface CreateProjectWithDocumentsOptions {
  /**
   * Required and explicit — there is no sensible default. The type is
   * caller/playbook driven (e.g. "flash_education"); forwarded verbatim to
   * `createProject`.
   */
  projectType: ProjectType;
  tags?: ProjectTag[];
  language?: string | null;
  isFlash?: boolean;
  savedSettingsId?: string | null;
  savedSettingsHasBrandKit?: boolean;
  extraMediaSettings?: Partial<ProjectInstanceSettingsRequest>;
  documentInput?: DocumentInput;
  /** Playbook whose add-on settings apply at creation (`playbook_id`). */
  playbookId?: string | null;
  /** Overrides the filename-derived title (e.g. the General settings card). */
  title?: string;
  description?: string;
  /**
   * Creates the project in this organization instead of the session's. Only
   * LTI needs it — LTI-launched users take the org from the launch URL, since
   * normal session state isn't reliable for them.
   */
  organizationId?: string;
}

/**
 * Shared orchestration for "create a project, then upload its documents to S3".
 *
 * This is the sequence currently copy-pasted across the creation entry points
 * (home DropZone, select-type, …): `createProject` → pre-signed S3 upload via
 * `useUploadFiles` → register the aggregated `all_documents.pdf` when there's
 * more than one file. It deliberately does NOT navigate, write the wizard store,
 * or handle paywalls — those are caller-specific. Callers layer their own
 * behavior on top and get a single, testable create+upload seam. New flows
 * should build on this; existing callers can migrate to it later.
 *
 * @param files Documents staged for the project, in order (first is primary).
 *   Owned by the caller so the same list drives the UI; this hook only mirrors
 *   it into the upload hook to derive the aggregated "all_documents.pdf" base.
 * @param hiddenDocKeys Keys (see `uploadFileKey`) of the files marked as
 *   Extras/knowledge-base-only — sent as each document's `hidden` flag.
 */
export const useCreateProjectWithDocuments = (
  files: UploadFile[],
  hiddenDocKeys: Set<string> = new Set()
) => {
  const apiUrl = useApiUrl();
  const { createProject } = useProjectCreation();

  const {
    getS3LinksToUploadFiles,
    uploadFileToS3,
    setFileList,
    aggregatedDocumentsBase,
  } = useUploadFiles({
    assetRequestProperties: (file) => {
      const isHidden = file ? hiddenDocKeys.has(uploadFileKey(file)) : false;
      return {
        document_type: "Unknown",
        // Extras opt OUT of the merge entirely — not just visually hidden,
        // but excluded from the all_documents.pdf index sequence too, so a
        // hidden doc sitting between two Source docs can't shift their
        // merge order (see useUploadFiles' per-file aggregate handling).
        aggregate_documents: !isHidden,
        hidden: isHidden,
      };
    },
    stagedAssets: files.map((f) => ({ uid: f.uid })),
  });

  // Keep the upload hook's internal fileList in sync so `aggregatedDocumentsBase`
  // is ready by the time the caller triggers creation.
  useEffect(() => {
    setFileList(files.map((f) => ({ ...f })));
  }, [files, setFileList]);

  const createProjectWithDocuments = useCallback(
    async ({
      projectType,
      tags = [],
      language = "auto",
      isFlash = false,
      savedSettingsId,
      savedSettingsHasBrandKit,
      extraMediaSettings,
      documentInput = "file",
      playbookId,
      title,
      description,
      organizationId,
    }: CreateProjectWithDocumentsOptions): Promise<MediaProject> => {
      const project = await createProject(
        files.map((f) => f.name),
        projectType,
        tags,
        language,
        isFlash,
        savedSettingsId,
        savedSettingsHasBrandKit,
        extraMediaSettings,
        documentInput,
        organizationId,
        playbookId,
        title,
        description
      );

      const customUrl = (fileName: string) =>
        `${apiUrl}/media/projects/${project.id}/documents/${fileName}`;
      // Clone so the files are mutable (they may originate from frozen state).
      const mutableFiles = files.map((f) => ({ ...f }));
      const s3Links = await getS3LinksToUploadFiles({
        fileListToUpload: mutableFiles,
        customUrl,
      });
      // Create every document entity (aggregate included) before any file hits S3.
      // Register the aggregated "all_documents.pdf" placeholder so the backend
      // can merge the uploaded docs into a single document. `aggregatedDocumentsBase`
      // only exists once 2+ files actually opt into the merge (see
      // useUploadFiles) — i.e. 2+ SOURCE docs; Extras don't count, however
      // many of them there are.
      if (aggregatedDocumentsBase) {
        await getS3LinksToUploadFiles({
          fileListToUpload: [aggregatedDocumentsBase],
          customUrl,
        });
      }

      await Promise.all(uploadFileToS3(mutableFiles, s3Links));

      return project;
    },
    [
      files,
      createProject,
      apiUrl,
      getS3LinksToUploadFiles,
      uploadFileToS3,
      aggregatedDocumentsBase,
    ]
  );

  return { createProjectWithDocuments };
};
